import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material'
import {
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import CompanyIcon from '../icons/CompanyIcon'
import DashboardIcon from '../icons/DashboardIcon'
import BillIcon from '../icons/BillIcon'
import LoadFormIcon from '../icons/LoadFormIcon'
import TargetIcon from '../icons/TargetIcon'
import CustomerIcon from '../icons/CustomerIcon'
import BookerIcon from '../icons/BookerIcon'
import SkuIcon from '../icons/SkuIcon'
import AreaIcon from '../icons/AreaIcon'
import ReportIcon from '../icons/ReportIcon'
import PurchaseInvoiceIcon from '../icons/PurchaseInvoiceIcon'
import InventoryIconCustom from '../icons/InventoryIcon'
import LedgerIcon from '../icons/LedgerIcon'
import ExpenseIcon from '../icons/ExpenseIcon'
import VendorIcon from '../icons/VendorIcon'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext'

const drawerWidth = 240

type NavItem = {
  text: string
  icon: React.ReactNode
  path: string
  disabled?: boolean
}

type NavSection = {
  label?: string
  items: NavItem[]
}

const sections: NavSection[] = [
  {
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      { text: 'Bills', icon: <BillIcon />, path: '/bills' },
      { text: 'Load Forms', icon: <LoadFormIcon />, path: '/loadforms' },
      { text: 'Targets', icon: <TargetIcon />, path: '/targets' },
    ],
  },
  {
    label: 'MASTER DATA',
    items: [
      { text: 'Customers', icon: <CustomerIcon />, path: '/customers' },
      { text: 'Bookers', icon: <BookerIcon />, path: '/users' },
      { text: 'SKUs', icon: <SkuIcon />, path: '/skus' },
      { text: 'Areas', icon: <AreaIcon />, path: '/areas' },
    ],
  },
  {
    label: 'FINANCE',
    items: [
      { text: 'Expenses', icon: <ExpenseIcon />, path: '/expenses' },
      { text: 'Account Ledger', icon: <LedgerIcon />, path: '/account-ledger' },
      { text: 'Reports', icon: <ReportIcon />, path: '/reports' },
    ],
  },
  {
    label: 'PURCHASING',
    items: [
      { text: 'Purchase Invoices', icon: <PurchaseInvoiceIcon />, path: '/purchase-invoices' },
      { text: 'Inventory', icon: <InventoryIconCustom />, path: '/inventory' },
      { text: 'Vendors', icon: <VendorIcon />, path: '/settings/vendors' },
    ],
  },
]
const devMenuItems: NavItem[] = [
  { text: 'Company Management', icon: <CompanyIcon />, path: '/companies', disabled: false },
]

interface SidebarProps {
  open: boolean
}

const Sidebar = ({ open }: SidebarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSelector((state: RootState) => state.auth)

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    item: NavItem | null
  } | null>(null)
  const { confirmLeave } = useUnsavedChanges()

  const handleContextMenu = (event: React.MouseEvent, item: NavItem) => {
    event.preventDefault()
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, item })
  }

  const handleClose = () => setContextMenu(null)

  const handleOpenInNewTab = (path: string) => {
    window.open(path, '_blank')
    handleClose()
  }

  const handleNavigate = (path: string) => {
    if (path !== location.pathname && !confirmLeave()) return
    navigate(path)
    handleClose()
  }

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : 0,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Brand header */}
      <Box sx={{ px: 2, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
        <Box
          component="img"
          src="/hisaab-logo.svg"
          alt="Hisaab logo"
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#2c2a26', fontSize: '0.9375rem', lineHeight: 1.2 }}>
            Hisaab
          </Typography>
          <Typography sx={{ color: '#7a766f', fontSize: '0.7rem', lineHeight: 1.2 }}>
            Admin panel
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
        {(user?.role === 'dev' ? [{ items: devMenuItems }] as NavSection[] : sections).map((section, sIdx) => (
          <Box key={section.label ?? sIdx}>
            {section.label && (
              <Typography
                sx={{
                  display: 'block',
                  px: 2.5,
                  pt: 2,
                  pb: 0.5,
                  color: '#7a766f',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                }}
              >
                {section.label}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map((item) => {
                const active = isActive(item.path)
                return (
                  <ListItem key={item.text} disablePadding sx={{ px: 1 }}>
                    <Tooltip
                      title={item.disabled ? 'Coming Soon' : 'Right-click for more options'}
                      placement="right"
                      arrow
                    >
                      <ListItemButton
                        selected={active}
                        disabled={item.disabled}
                        onClick={() => !item.disabled && handleNavigate(item.path)}
                        onContextMenu={(e) => !item.disabled && handleContextMenu(e, item)}
                        sx={{
                          borderRadius: 1,
                          py: 0.75,
                          '&.Mui-selected': {
                            bgcolor: '#e8f5ee',
                            color: '#2d6a4f',
                            '&:hover': { bgcolor: '#d4ede0' },
                            '& .MuiListItemIcon-root': { color: '#2d6a4f' },
                          },
                          '&:hover': {
                            bgcolor: item.disabled ? 'transparent' : '#eeebe5',
                          },
                          '& .MuiListItemIcon-root > .MuiSvgIcon-root': {
                            transition: 'transform 180ms ease',
                          },
                          '&:hover .MuiListItemIcon-root > .MuiSvgIcon-root': {
                            transform: 'translateY(-2px) scale(1.06)',
                          },
                          '@media (prefers-reduced-motion: reduce)': {
                            '& .MuiListItemIcon-root > .MuiSvgIcon-root': {
                              transition: 'none',
                            },
                            '&:hover .MuiListItemIcon-root > .MuiSvgIcon-root': {
                              transform: 'none',
                            },
                          },
                          cursor: item.disabled ? 'not-allowed' : 'pointer',
                          opacity: item.disabled ? 0.4 : 1,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: active ? '#2d6a4f' : '#7a766f',
                            opacity: item.disabled ? 0.4 : 1,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}
                          sx={{ opacity: item.disabled ? 0.4 : 1 }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                )
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Brand footer */}
      <Divider />
      <Box sx={{ px: 2, py: 2, textAlign: 'center', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          © Powered by Hisaab
        </Typography>
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        <MenuItem onClick={() => contextMenu?.item && handleOpenInNewTab(contextMenu.item.path)}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu?.item && handleNavigate(contextMenu.item.path)}>
          <ListItemIcon>
            {contextMenu?.item?.icon}
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
      </Menu>
    </Drawer>
  )
}

export default Sidebar
